/**
 * ─── Brayhard: Software Hardening & Error Handling Utilities ─────────
 * Establishes human-friendly error interception, timeout wrappers,
 * and graceful fallback patterns for production resiliency.
 */

export interface BrayhardErrorMapping {
  userMessage: string
  action: string
  code: string
  rawError?: unknown
}

/**
 * Maps raw backend, database, or sync errors to human-friendly visual output and recommended actions.
 */
export function mapErrorToUserMessage(error: unknown): BrayhardErrorMapping {
  const err = error as Record<string, any> | undefined
  const message = String(err?.message || err || '')
  const status = Number(err?.status || err?.statusCode || 0)
  const code = String(err?.code || '')

  // 1. PGRST205 / Schema Cache
  if (code === 'PGRST205' || message.includes('schema cache') || message.includes('Could not find table')) {
    return {
      userMessage: "We've updated our system. Let's refresh your local database setup.",
      action: 'Reload/Sync Button',
      code: 'SCHEMA_CACHE_UPDATE',
      rawError: error,
    }
  }

  // 2. 502 / 503 / Bad Gateway
  if (status === 502 || status === 503 || message.includes('Bad Gateway') || message.includes('Service Unavailable') || message.includes('temporarily busy')) {
    return {
      userMessage: 'The sync server is temporarily busy. Your offline entries are safe.',
      action: 'Retry in 10s',
      code: 'SERVER_BUSY',
      rawError: error,
    }
  }

  // 3. Auth Session Expiry / JWT / 401
  if (status === 401 || message.includes('JWT') || message.includes('session') || message.includes('expired') || message.includes('unauthorized') || code === 'AUTH_ERROR') {
    return {
      userMessage: 'Your security session has expired. Please sign in again to resume.',
      action: 'Go to Login',
      code: 'AUTH_EXPIRED',
      rawError: error,
    }
  }

  // 4. Network Timeout / Network error
  if (status === 0 || code === 'NETWORK_ERROR' || message.includes('Network request failed') || message.includes('timeout') || message.includes('Failed to fetch') || message.includes('Offline')) {
    return {
      userMessage: 'Connection timed out. We are switching to offline-first local mode.',
      action: 'Work Offline',
      code: 'NETWORK_OFFLINE_FALLBACK',
      rawError: error,
    }
  }

  // 5. Concurrent modification / Conflict
  if (status === 409 || code === 'CONFLICT' || message.includes('Concurrent modification')) {
    return {
      userMessage: 'This record was modified on another device. We have merged the latest version.',
      action: 'Review Changes',
      code: 'CONCURRENT_CONFLICT',
      rawError: error,
    }
  }

  // 6. Database / Local storage error
  if (code === 'DB_ERROR' || message.includes('SQLite') || message.includes('Dexie') || message.includes('database')) {
    return {
      userMessage: 'We encountered a local database issue. Your data remains protected.',
      action: 'Refresh Database',
      code: 'LOCAL_DB_ISSUE',
      rawError: error,
    }
  }

  // Default fallback
  return {
    userMessage: 'An unexpected error occurred. Your offline data remains safe.',
    action: 'Retry',
    code: 'GENERAL_FALLBACK',
    rawError: error,
  }
}

/**
 * Wraps synchronous or asynchronous operations with human-friendly error interception
 * without leaking raw stack traces to the UI.
 */
export async function safeExecuteSync<T>(
  operation: () => Promise<T> | T
): Promise<{ success: boolean; data?: T; userMessage: string; action: string; rawError?: unknown }> {
  try {
    const data = await operation()
    return { success: true, data, userMessage: 'Sync complete', action: 'None' }
  } catch (error: unknown) {
    console.error('[Brayhard Exception Log]:', error)
    const mapped = mapErrorToUserMessage(error)
    return {
      success: false,
      userMessage: mapped.userMessage,
      action: mapped.action,
      rawError: error,
    }
  }
}

/**
 * Wraps an async operation with a timeout (default 15s) and optional graceful fallback value.
 * Fulfills Brayhard Pillar 4 (JWT & Auth Session Hardening / Timeout Wrappers).
 */
export async function withTimeout<T>(
  operation: Promise<T> | (() => Promise<T>),
  timeoutMs = 15000,
  fallbackValue?: T
): Promise<T> {
  const promise = typeof operation === 'function' ? operation() : operation

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`Operation timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  try {
    const result = await Promise.race([promise, timeoutPromise])
    if (timer) clearTimeout(timer)
    return result
  } catch (error) {
    if (timer) clearTimeout(timer)
    if (fallbackValue !== undefined) {
      console.warn('[Brayhard Timeout Fallback]: Operation timed out, switching to local fallback state.')
      return fallbackValue
    }
    throw error
  }
}
